import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/SiteShell";
import { PageHero } from "@/components/PageHero";
import { useLocale } from "@/contexts/LocaleContext";
import { DICTS } from "@/lib/i18n";
import { useHeroVideoUrls } from "@/hooks/useHeroVideoUrls";
import editorial1 from "@/assets/editorial-1.jpg";
import { JOURNAL_POSTS, getCategoryLabel } from "@/data/journal-posts";
import { PUBLIC_SITE_ORIGIN } from "@/lib/public-origin";

import { publicCacheHeaders } from "@/lib/public-cache-headers";

export const Route = createFileRoute("/journal/")({
  headers: publicCacheHeaders,
  head: () => ({
    meta: [
      { title: DICTS.en["journal.meta.title"] },
      { name: "description", content: DICTS.en["journal.meta.description"] },
      { property: "og:title", content: DICTS.en["journal.meta.title"] },
      { property: "og:description", content: DICTS.en["journal.meta.description"] },
      { property: "og:url", content: `${PUBLIC_SITE_ORIGIN}/journal` },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: `${PUBLIC_SITE_ORIGIN}/journal` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          name: DICTS.en["journal.meta.title"],
          description: DICTS.en["journal.meta.description"],
          url: `${PUBLIC_SITE_ORIGIN}/journal`,
          publisher: { "@type": "Organization", name: "Lecmo Parfums", url: `${PUBLIC_SITE_ORIGIN}/` },
          blogPost: JOURNAL_POSTS.map((p) => ({
            "@type": "BlogPosting",
            headline: p.en.title,
            description: p.en.excerpt,
            datePublished: p.publishedAt,
            url: `${PUBLIC_SITE_ORIGIN}/journal/${p.slug}`,
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Lecmo", item: `${PUBLIC_SITE_ORIGIN}/` },
            { "@type": "ListItem", position: 2, name: "Journal", item: `${PUBLIC_SITE_ORIGIN}/journal` },
          ],
        }),
      },
    ],
  }),
  component: JournalIndexPage,
});

function JournalIndexPage() {
  const heroVideo = useHeroVideoUrls("journal");
  const { t, locale } = useLocale();
  const lang = locale === "ar" ? "ar" : "en";

  return (
    <SiteShell>
      <PageHero
        pageKey="journal"
        title={t("journal.title")}
        subtitle={t("journal.subtitle")}
        videoUrl={heroVideo.desktop}
        mobileVideoUrl={heroVideo.mobile}
        campaignId={heroVideo.campaignId}
        image={heroVideo.poster || editorial1}
        alt={t("journal.title")}
      />

      <section className="mx-auto w-full max-w-[1440px] px-4 py-20 sm:px-6 md:py-24">
        <div className="grid grid-cols-1 border-t border-border md:grid-cols-2 lg:grid-cols-3">
          {JOURNAL_POSTS.map((p) => {
            const content = p[lang];
            return (
              <Link
                key={p.slug}
                to="/journal/$slug"
                params={{ slug: p.slug }}
                className="group block border-b border-border p-6 md:border-e md:p-8 md:last:border-e-0 md:[&:nth-child(3n)]:border-e-0"
              >
                <div className="aspect-[4/5] overflow-hidden bg-secondary">
                  <img
                    src={p.coverImage}
                    alt={content.title}
                    width={1280}
                    height={1600}
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                    loading="lazy"
                  />
                </div>
                <p className="mt-5 t-eyebrow tracking-[var(--tracking-wider)] text-muted-foreground">
                  {getCategoryLabel(p.category, lang)} · {p.minutes} {lang === "ar" ? "دقائق" : "min"}
                </p>
                <h2 className="mt-3 font-serif text-2xl leading-tight tracking-[-0.01em]">
                  {content.title}
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {content.excerpt}
                </p>
                <span className="mt-5 inline-block t-eyebrow uppercase tracking-[var(--tracking-wider)] underline underline-offset-[6px] decoration-[0.5px] group-hover:decoration-2">
                  {t("cta.read")}
                </span>
              </Link>
            );
          })}
        </div>
      </section>
    </SiteShell>
  );
}